import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Panel,
  PanelType,
  Pivot,
  PivotItem,
  Stack,
  Text,
  TextField,
  SearchBox,
  Label,
  Toggle,
  Checkbox,
  ChoiceGroup,
  IChoiceGroupOption,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Callout,
  DirectionalHint,
  Dialog,
  DialogType,
  DialogFooter,
  mergeStyleSets,
} from "@fluentui/react";

import { accessService } from "../services/accessService";
import { graphService, type GraphUser, type GraphGroup } from "../services/graphService";

// Unified search hit — both user and group searches collapse onto this shape
// so the dropdown UI doesn't need to know which kind it is rendering.
type SearchHit = { upn: string; displayName: string };

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type DomainCatalogEntry = {
  domain: string;
  display_name: string;
  has_chat: boolean;
  has_dashboard: boolean;
  has_build: boolean;
  has_dataset: boolean;
  has_geospatial: boolean;
  is_sensitive: boolean;
  sort_order: number;
};

type AccessUser = {
  upn: string;
  entity_type?: "user" | "group";
  display_name?: string | null;
  is_app_admin: boolean;
  surfaces: { domain: string; surface: string }[];
  finance_full_access: boolean;
  finance_directorates: string[];
  finance_departments: string[];
  monthly_limit_dollars?: number | string | null;
  is_active?: boolean;
};

type FinanceOptions = {
  directorates: string[];
  departmentsByDirectorate: Record<string, string[]>;
};

type FinanceMode = "full" | "restricted" | "none";

type TabCatalogEntry = {
  tab: string;
  item: string;
  display_name: string;
  classification: "public" | "governed" | "admin_only";
  honors_hierarchy: boolean;
  category: string | null;
  // 'pii' surfaces a red badge in the row so admins know they're granting
  // access to personally identifiable data. Null for ordinary items.
  sensitivity?: "pii" | "restricted" | null;
  display_order: number;
  active: boolean;
};

type UserTabGrant = { tab: string; item: string };

type Props = {
  open: boolean;
  // 'add'      — add a user; AAD search hits Graph users
  // 'addGroup' — add an Entra security group; AAD search hits Graph groups
  // 'addAdmin' — focused: grant App admin role to a user (no per-tab grants)
  // 'edit'     — edit existing user/group access
  mode: "add" | "addGroup" | "addAdmin" | "edit";
  user: AccessUser | null;
  domainCatalog: DomainCatalogEntry[]; // retained for prop compat; not used in tab-driven panel
  onClose: () => void;
  onSaved: () => void;
};

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

// Tab strip order — mirrors the portal's top nav. Labels chosen to match the
// approved preview HTML; keys must match access.tab_catalog.tab values.
const TAB_ORDER: { key: string; label: string }[] = [
  { key: "chat",            label: "Chat" },
  { key: "datasets",        label: "Datasets" },
  { key: "dashboards",      label: "Dashboards" },
  { key: "build_dashboard", label: "Build Your Dashboard" },
  { key: "search",          label: "Search" },
  { key: "geospatial",      label: "Geospatial" },
  { key: "analytics",       label: "Analytics" },
  { key: "realtime",        label: "Real Time" },
  { key: "admin",           label: "Admin" },
];

const FINANCE_MODE_OPTIONS: IChoiceGroupOption[] = [
  { key: "full",       text: "Full — sees all directorates" },
  { key: "restricted", text: "Restricted — only listed directorates / departments" },
  { key: "none",       text: "None — no finance data visible" },
];

const grantKey = (tab: string, item: string) => `${tab}:${item}`;

// ─────────────────────────────────────────────────────────────────────────────
// Styles — tokens align with the admin redesign (PR #35-#38).
// ─────────────────────────────────────────────────────────────────────────────

const styles = mergeStyleSets({
  panelBody: {
    paddingTop: 4,
    paddingBottom: 80, // leaves room for the sticky footer
  },

  // Identity row at top: UPN + Display name + admin toggle + budget
  identityRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginBottom: 14,
  },
  settingsRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: 12,
    alignItems: "stretch",
    marginBottom: 18,
  },
  settingsCard: {
    flex: "1 1 220px",
    minWidth: 220,
    padding: "10px 14px",
    border: "1px solid #EDEDED",
    borderRadius: 6,
    background: "#FAFAFA",
    display: "flex",
    flexDirection: "column" as const,
    gap: 4,
  },
  settingsCardLabel: {
    fontSize: 12,
    color: "#5C5C5C",
    fontWeight: 600,
  },
  settingsCardHint: {
    fontSize: 11,
    color: "#7A7A7A",
  },

  // Public auto-grant strip
  publicRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: 6,
    padding: "10px 12px",
    background: "#DFF6DD",
    border: "1px solid #C9ECCC",
    borderRadius: 4,
    alignItems: "center",
    marginBottom: 14,
  },
  publicTag: {
    fontSize: 10.5,
    color: "#107C10",
    fontWeight: 700,
    letterSpacing: 0.4,
    marginRight: 6,
  },
  chip: {
    background: "#FFFFFF",
    color: "#232323",
    padding: "2px 9px",
    borderRadius: 10,
    border: "1px solid #C9ECCC",
    fontSize: 12.5,
    fontWeight: 500,
  },

  // Governed grant card
  grantCard: {
    border: "1px solid #EDEDED",
    borderRadius: 4,
    padding: "12px 14px",
    marginBottom: 6,
    transition: "border-color 0.1s, background 0.1s",
  },
  grantCardChecked: {
    borderColor: "#0078D4",
    background: "#F3F9FD",
  },
  grantRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  grantText: { flex: 1, display: "flex", flexDirection: "column" as const },
  grantTitle: { fontSize: 13.5, color: "#232323", fontWeight: 600 },
  grantDesc: { fontSize: 12, color: "#6B6B6B", marginTop: 1 },
  hierarchyBadge: {
    fontSize: 10.5,
    color: "#856B00",
    background: "#FFF4CE",
    border: "1px solid #F9D774",
    padding: "1px 7px",
    borderRadius: 9,
    fontWeight: 500,
    whiteSpace: "nowrap" as const,
  },

  // Inline finance hierarchy block
  hierarchyBlock: {
    borderTop: "1px dashed #D2D0CE",
    paddingTop: 12,
    marginTop: 12,
    display: "flex",
    flexDirection: "column" as const,
    gap: 10,
  },
  hierarchyHeader: { fontWeight: 600, fontSize: 13, color: "#232323" },
  hierarchyNote: { fontSize: 11.5, color: "#6B6B6B", marginTop: -6 },

  // Cross-tab scope reference pill
  scopePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    background: "#F3F9FD",
    border: "1px solid #C7E0F4",
    borderRadius: 4,
    fontSize: 12,
    color: "#232323",
    marginBottom: 10,
  },
  scopePillLabel: { color: "#6B6B6B", fontWeight: 500 },
  scopePillValue: { color: "#0078D4", fontWeight: 600 },

  // Empty states for atomic tabs (Geospatial / Realtime / Analytics / Admin)
  emptyState: {
    padding: "32px 24px",
    textAlign: "center" as const,
    color: "#6B6B6B",
    border: "1px dashed #D2D0CE",
    borderRadius: 4,
    background: "#FAFAFA",
  },
  emptyIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    background: "#DFF6DD",
    color: "#107C10",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
    fontSize: 22,
    fontWeight: 600,
  },
  emptyIconAdmin: { background: "#FFF4CE", color: "#856B00" },
  emptyTitle: { color: "#232323", fontWeight: 600, display: "block", marginBottom: 4, fontSize: 14 },
  emptySub: { fontSize: 12.5 },

  // Tree (finance scope)
  tree: {
    border: "1px solid #EDEDED",
    borderRadius: 4,
    background: "#FFFFFF",
    maxHeight: 220,
    overflowY: "auto" as const,
  },
  treeRow: {
    padding: "6px 12px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    borderBottom: "1px solid #F7F6F4",
  },
  treeRowDirectorate: { fontWeight: 600 },
  treeRowDepartment: { paddingLeft: 36, color: "#5C5C5C", fontWeight: "normal" as const },
  treeCovered: { fontSize: 10.5, color: "#8A8886", marginLeft: "auto" },

  // AAD search results dropdown
  resultsList: {
    background: "white",
    border: "1px solid #E1E1E1",
    borderRadius: 4,
    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
    maxHeight: 280,
    overflow: "auto",
    width: 380,
    padding: 4,
  },
  resultRow: {
    padding: "8px 12px",
    cursor: "pointer",
    borderRadius: 4,
    selectors: { "&:hover": { background: "#F4F5F6" } },
  },
  resultName: { fontSize: 13, color: "#232323", fontWeight: 600 },
  resultUpn: { fontSize: 11, color: "#7A7A7A" },

  panelFooter: {
    position: "absolute" as const,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 16px",
    borderTop: "1px solid #E1E1E1",
    background: "#FFFFFF",
    zIndex: 1,
  },
  footerDirty: { flex: 1, color: "#0078D4", fontWeight: 600, fontSize: 12.5 },
  footerClean: { flex: 1, color: "#6B6B6B", fontSize: 12.5 },
});

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function deriveInitialMode(user: AccessUser | null): FinanceMode {
  if (!user) return "none";
  if (user.finance_full_access) return "full";
  if (user.finance_directorates.length || user.finance_departments.length) return "restricted";
  return "none";
}

function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

function arrEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  for (let i = 0; i < sa.length; i++) if (sa[i] !== sb[i]) return false;
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Finance hierarchy scope tree (reused from PR #38 design)
// ─────────────────────────────────────────────────────────────────────────────

const ScopeTree: React.FC<{
  financeOptions: FinanceOptions | null;
  selectedDirectorates: string[];
  selectedDepartments: string[];
  onToggleDirectorate: (name: string) => void;
  onToggleDepartment: (name: string) => void;
}> = ({ financeOptions, selectedDirectorates, selectedDepartments, onToggleDirectorate, onToggleDepartment }) => {
  if (!financeOptions) {
    return (
      <div className={styles.tree}>
        <div className={styles.treeRow}>
          <Spinner size={SpinnerSize.xSmall} /> <Text variant="small">Loading directorates…</Text>
        </div>
      </div>
    );
  }
  const dirSet = new Set(selectedDirectorates);
  const deptSet = new Set(selectedDepartments);
  const directorates = [...financeOptions.directorates].sort();
  return (
    <div className={styles.tree}>
      {directorates.map((dir) => {
        const depts = (financeOptions.departmentsByDirectorate[dir] ?? []).slice().sort();
        const dirChecked = dirSet.has(dir);
        return (
          <React.Fragment key={dir}>
            <div className={`${styles.treeRow} ${styles.treeRowDirectorate}`}>
              <Checkbox
                checked={dirChecked}
                onChange={() => onToggleDirectorate(dir)}
                label={dir}
              />
            </div>
            {depts.map((dept) => {
              const deptChecked = deptSet.has(dept);
              const covered = dirChecked;
              return (
                <div
                  key={dept}
                  className={`${styles.treeRow} ${styles.treeRowDepartment}`}
                >
                  <Checkbox
                    checked={covered || deptChecked}
                    disabled={covered}
                    onChange={() => onToggleDepartment(dept)}
                    label={dept}
                  />
                  {covered && <span className={styles.treeCovered}>covered by parent</span>}
                </div>
              );
            })}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main panel
// ─────────────────────────────────────────────────────────────────────────────

const EditAccessPanel: React.FC<Props> = ({ open, mode, user, onClose, onSaved }) => {
  // Identity
  const [upn, setUpn] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [entityType, setEntityType] = useState<"user" | "group">("user");

  // Settings
  const [isAppAdmin, setIsAppAdmin] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [monthlyBudget, setMonthlyBudget] = useState<string>("");

  // Tab access state
  const [tabCatalog, setTabCatalog] = useState<TabCatalogEntry[]>([]);
  const [grants, setGrants] = useState<Set<string>>(new Set()); // governed grants only
  const [activeTab, setActiveTab] = useState<string>("chat");
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  // Finance scope (single per-user; applied wherever Finance is granted)
  const [financeMode, setFinanceMode] = useState<FinanceMode>("none");
  const [selectedDirectorates, setSelectedDirectorates] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [financeOptions, setFinanceOptions] = useState<FinanceOptions | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  // AAD search (add modes)
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [graphError, setGraphError] = useState<string | null>(null);
  const searchBoxRef = useRef<HTMLDivElement | null>(null);

  // Save / dialog
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Snapshot for dirty detection + revert reset
  type FormSnapshot = {
    upn: string;
    displayName: string;
    entityType: "user" | "group";
    isAppAdmin: boolean;
    isActive: boolean;
    monthlyBudget: string;
    grants: string[]; // sorted "tab:item" strings for stable equality
    financeMode: FinanceMode;
    selectedDirectorates: string[];
    selectedDepartments: string[];
  };
  const [snapshot, setSnapshot] = useState<FormSnapshot | null>(null);

  const isAddMode = mode === "add" || mode === "addGroup" || mode === "addAdmin";
  const isAdminFlow = mode === "addAdmin";

  // ───── Hydrate when panel opens ─────
  useEffect(() => {
    if (!open) return;

    const initUpn = user?.upn ?? "";
    const initDisplay = user?.display_name ?? "";
    const initEntity: "user" | "group" =
      mode === "addGroup"
        ? "group"
        : mode === "addAdmin" || mode === "add"
        ? "user"
        : user?.entity_type ?? "user";
    const initAdmin = mode === "addAdmin" ? true : user?.is_app_admin ?? false;
    const initBudget =
      user?.monthly_limit_dollars !== null && user?.monthly_limit_dollars !== undefined
        ? String(user.monthly_limit_dollars)
        : "";
    const initMode = deriveInitialMode(user);
    const initDirs = user?.finance_directorates ?? [];
    const initDepts = user?.finance_departments ?? [];

    setUpn(initUpn);
    setDisplayName(initDisplay);
    setEntityType(initEntity);
    setIsAppAdmin(initAdmin);
    setIsActive(user?.is_active ?? true);
    setMonthlyBudget(initBudget);
    setFinanceMode(initMode);
    setSelectedDirectorates(initDirs);
    setSelectedDepartments(initDepts);
    setActiveTab("chat");

    setSearchQuery("");
    setSearchResults([]);
    setShowResults(false);
    setGraphError(null);
    setError(null);
    setDiscardDialogOpen(false);

    // Tab grants come from a separate endpoint — fetched below in another effect
    // because it depends on the resolved UPN (not just user prop).
  }, [open, user, mode]);

  // ───── Load catalog + tab grants when panel opens or upn changes ─────
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCatalogLoading(true);
    setCatalogError(null);

    const loadCatalog = accessService.get<TabCatalogEntry[]>("/admin/access/tab-catalog");
    const loadGrants = upn
      ? accessService.get<UserTabGrant[]>(`/admin/access/user/${encodeURIComponent(upn)}/tabs`)
      : Promise.resolve({ data: [] as UserTabGrant[] });

    Promise.all([loadCatalog, loadGrants])
      .then(([catRes, grantsRes]) => {
        if (cancelled) return;
        const cat = (catRes.data || []).slice().sort((a, b) => {
          const order = TAB_ORDER.findIndex((t) => t.key === a.tab) - TAB_ORDER.findIndex((t) => t.key === b.tab);
          if (order !== 0) return order;
          return a.display_order - b.display_order;
        });
        setTabCatalog(cat);
        const set = new Set<string>(grantsRes.data.map((g) => grantKey(g.tab, g.item)));
        setGrants(set);
        // Snapshot for dirty detection
        setSnapshot({
          upn,
          displayName: user?.display_name ?? "",
          entityType:
            mode === "addGroup"
              ? "group"
              : mode === "addAdmin" || mode === "add"
              ? "user"
              : user?.entity_type ?? "user",
          isAppAdmin: mode === "addAdmin" ? true : user?.is_app_admin ?? false,
          isActive: user?.is_active ?? true,
          monthlyBudget:
            user?.monthly_limit_dollars !== null && user?.monthly_limit_dollars !== undefined
              ? String(user.monthly_limit_dollars)
              : "",
          grants: [...set].sort(),
          financeMode: deriveInitialMode(user),
          selectedDirectorates: [...(user?.finance_directorates ?? [])].sort(),
          selectedDepartments: [...(user?.finance_departments ?? [])].sort(),
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error ??
          (err as { message?: string })?.message ??
          "Failed to load tab catalogue";
        setCatalogError(msg);
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, upn, mode, user]);

  // ───── Load directorate / department options for the scope tree ─────
  useEffect(() => {
    if (!open) return;
    setOptionsError(null);
    accessService
      .get<FinanceOptions>("/admin/access/finance-options")
      .then((r) => setFinanceOptions(r.data))
      .catch((err: unknown) => {
        const msg =
          (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error ??
          (err as { message?: string })?.message ??
          "Failed to load directorate / department options";
        setOptionsError(msg);
      });
  }, [open]);

  // ───── AAD search (add modes only) ─────
  useEffect(() => {
    if (!isAddMode || !searchQuery.trim() || searchQuery.trim().length < 2) {
      setSearchResults([]);
      setShowResults(false);
      setGraphError(null);
      return;
    }
    if (upn) return; // already picked
    setSearching(true);
    setGraphError(null);
    const handle = setTimeout(async () => {
      try {
        const hits: SearchHit[] =
          entityType === "group"
            ? (await graphService.searchGroups(searchQuery)).map((g: GraphGroup) => ({
                upn: g.id,
                displayName: g.displayName,
              }))
            : (await graphService.searchUsers(searchQuery)).map((u: GraphUser) => ({
                upn: u.userPrincipalName,
                displayName: u.displayName,
              }));
        setSearchResults(hits);
        setShowResults(true);
      } catch (err: unknown) {
        const msg = (err as { message?: string })?.message ?? "Search failed";
        setGraphError(msg);
        setSearchResults([]);
        setShowResults(false);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [searchQuery, entityType, upn, isAddMode]);

  const pickSearchResult = useCallback((r: SearchHit) => {
    setUpn(r.upn);
    setDisplayName(r.displayName);
    setSearchQuery(r.displayName);
    setShowResults(false);
  }, []);

  // ───── Catalog projections ─────
  const catalogByTab = useMemo(() => {
    const out = new Map<string, TabCatalogEntry[]>();
    for (const c of tabCatalog) {
      if (!c.active) continue;
      if (!out.has(c.tab)) out.set(c.tab, []);
      out.get(c.tab)!.push(c);
    }
    return out;
  }, [tabCatalog]);

  // Count of governed grants per tab — drives the count badges in the Pivot.
  const tabGovernedCounts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const c of tabCatalog) {
      if (c.classification !== "governed" || !c.active) continue;
      if (grants.has(grantKey(c.tab, c.item))) out[c.tab] = (out[c.tab] ?? 0) + 1;
    }
    return out;
  }, [grants, tabCatalog]);

  // ───── Toggle handlers ─────
  const toggleGrant = useCallback((tab: string, item: string, checked: boolean) => {
    setGrants((prev) => {
      const next = new Set(prev);
      const k = grantKey(tab, item);
      if (checked) next.add(k);
      else next.delete(k);
      return next;
    });
  }, []);

  const toggleDirectorate = useCallback((name: string) => {
    setSelectedDirectorates((prev) =>
      prev.includes(name) ? prev.filter((d) => d !== name) : [...prev, name]
    );
  }, []);
  const toggleDepartment = useCallback((name: string) => {
    setSelectedDepartments((prev) =>
      prev.includes(name) ? prev.filter((d) => d !== name) : [...prev, name]
    );
  }, []);

  // ───── Dirty detection ─────
  const dirtyCount = useMemo(() => {
    if (!snapshot) return 0;
    let c = 0;
    if (upn !== snapshot.upn) c++;
    if (displayName !== snapshot.displayName) c++;
    if (entityType !== snapshot.entityType) c++;
    if (isAppAdmin !== snapshot.isAppAdmin) c++;
    if (isActive !== snapshot.isActive) c++;
    if (monthlyBudget !== snapshot.monthlyBudget) c++;
    if (!setsEqual(grants, new Set(snapshot.grants))) c++;
    if (financeMode !== snapshot.financeMode) c++;
    if (!arrEqual(selectedDirectorates, snapshot.selectedDirectorates)) c++;
    if (!arrEqual(selectedDepartments, snapshot.selectedDepartments)) c++;
    return c;
  }, [snapshot, upn, displayName, entityType, isAppAdmin, isActive, monthlyBudget, grants, financeMode, selectedDirectorates, selectedDepartments]);

  // ───── Save ─────
  const handleSave = useCallback(async () => {
    if (!upn) {
      setError("UPN is required.");
      return;
    }
    setSaving(true);
    setError(null);
    const enc = encodeURIComponent(upn);
    try {
      const tasks: Promise<unknown>[] = [];

      // ─── addAdmin: focused single-purpose flow ──────────────────────────
      // Only writes the admin grant. Snapshot-diff doesn't apply here — the
      // panel is opened *to* create an admin, so the POST always fires.
      if (mode === "addAdmin") {
        tasks.push(
          accessService.post(
            `/admin/access/admins/${enc}`,
            displayName ? { displayName } : {}
          )
        );
        await Promise.all(tasks);
        onSaved();
        onClose();
        return;
      }

      // ─── App admin toggle (edit-user only — toggle is hidden elsewhere) ─
      if (mode === "edit" && entityType === "user" && snapshot && isAppAdmin !== snapshot.isAppAdmin) {
        tasks.push(
          isAppAdmin
            ? accessService.post(`/admin/access/admins/${enc}`, displayName ? { displayName } : {})
            : accessService.delete(`/admin/access/admins/${enc}`)
        );
      }

      // ─── Active / Disabled toggle (edit-user only). Writes access.user_status.
      //     requireUser middleware enforces the flag at every authenticated request.
      if (mode === "edit" && entityType === "user" && snapshot && isActive !== snapshot.isActive) {
        tasks.push(
          accessService.put(`/admin/access/user/${enc}/active`, { isActive })
        );
      }

      // ─── Tab grants — governed items only; public/admin_only resolve via
      //     classification, not via user_tab_access rows ──────────────────
      const governedKeys = new Set<string>();
      for (const c of tabCatalog) {
        if (c.classification === "governed" && c.active) governedKeys.add(grantKey(c.tab, c.item));
      }
      const desiredGrants: UserTabGrant[] = [...grants]
        .filter((k) => governedKeys.has(k))
        .map((k) => {
          const [tab, item] = k.split(":");
          return { tab, item };
        });
      // Always write tab grants in add modes (even if empty — establishes the
      // identity row); in edit mode, write only on diff.
      if (mode === "add" || mode === "addGroup" || (snapshot && !setsEqual(grants, new Set(snapshot.grants)))) {
        tasks.push(
          accessService.put(`/admin/access/user/${enc}/tabs`, {
            grants: desiredGrants,
            entityType,
            displayName: displayName || null,
          })
        );
      }

      // ─── Token budget — supported for both users and groups (Phase 3f) ─
      if (mode === "add" || mode === "addGroup" || (snapshot && monthlyBudget !== snapshot.monthlyBudget)) {
        const trimmed = monthlyBudget.trim();
        const dollarsValue = trimmed === "" ? null : Number(trimmed);
        if (trimmed !== "" && (Number.isNaN(dollarsValue!) || dollarsValue! < 0)) {
          throw new Error("Monthly budget must be a non-negative number or blank.");
        }
        tasks.push(
          accessService.put(`/admin/access/user/${enc}/token-budget`, {
            dollars: dollarsValue,
            entityType,
            displayName: displayName || null,
          })
        );
      }

      // ─── Finance scope — user-only (data model rejects group rows) ─────
      if (entityType === "user") {
        if (!snapshot || financeMode !== snapshot.financeMode) {
          tasks.push(
            accessService.put(`/admin/access/user/${enc}/finance-scope`, { mode: financeMode })
          );
        }
        if (financeMode !== "none") {
          if (!snapshot || !arrEqual(selectedDirectorates, snapshot.selectedDirectorates)) {
            tasks.push(
              accessService.put(`/admin/access/user/${enc}/directorates`, {
                directorates: selectedDirectorates,
              })
            );
          }
          if (!snapshot || !arrEqual(selectedDepartments, snapshot.selectedDepartments)) {
            tasks.push(
              accessService.put(`/admin/access/user/${enc}/departments`, {
                departments: selectedDepartments.filter((d) => !selectedDirectorates.includes(d)),
              })
            );
          }
        }
      }

      await Promise.all(tasks);
      onSaved();
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error ??
        (err as { message?: string })?.message ??
        "Save failed";
      setError(msg);
    } finally {
      setSaving(false);
    }
  }, [mode, upn, displayName, entityType, isAppAdmin, isActive, monthlyBudget, grants, financeMode, selectedDirectorates, selectedDepartments, snapshot, tabCatalog, onSaved, onClose]);

  const handleDismiss = useCallback(() => {
    if (dirtyCount > 0) {
      setDiscardDialogOpen(true);
      return;
    }
    onClose();
  }, [dirtyCount, onClose]);

  // ───── Hard-delete the user ─────
  // Wipes the entire access footprint across access.* tables (audit kept).
  // Confirmation is the responsibility of the dialog; this fires the call.
  const handleDelete = useCallback(async () => {
    if (!upn) return;
    setDeleting(true);
    setError(null);
    try {
      await accessService.delete(`/admin/access/user/${encodeURIComponent(upn)}`);
      setDeleteDialogOpen(false);
      onSaved();
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error ??
        (err as { message?: string })?.message ??
        "Delete failed";
      setError(msg);
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  }, [upn, onSaved, onClose]);

  // ───── Tab content renderers ─────

  const financeScopeSummary = useMemo(() => {
    if (financeMode === "full") return "Full council";
    if (financeMode === "none") return "None";
    const dCount = selectedDirectorates.length;
    const deptCount = selectedDepartments.filter((d) => !selectedDirectorates.includes(d)).length;
    if (dCount === 0 && deptCount === 0) return "Restricted (none selected)";
    if (dCount > 0 && dCount <= 2 && deptCount === 0) {
      return `Restricted to ${selectedDirectorates.join(", ")}`;
    }
    const parts: string[] = [];
    if (dCount) parts.push(`${dCount} directorate${dCount === 1 ? "" : "s"}`);
    if (deptCount) parts.push(`${deptCount} department${deptCount === 1 ? "" : "s"}`);
    return `Restricted to ${parts.join(", ")}`;
  }, [financeMode, selectedDirectorates, selectedDepartments]);

  const renderHierarchyEditor = () => (
    <div className={styles.hierarchyBlock}>
      <Text className={styles.hierarchyHeader}>Finance hierarchy scope</Text>
      <Text className={styles.hierarchyNote}>
        Single per-user scope — applied wherever Finance is granted (Chat, Datasets, Dashboards, Build).
      </Text>
      <ChoiceGroup
        selectedKey={financeMode}
        options={FINANCE_MODE_OPTIONS}
        onChange={(_, opt) => opt && setFinanceMode(opt.key as FinanceMode)}
      />
      {financeMode === "restricted" && (
        <>
          {optionsError && <MessageBar messageBarType={MessageBarType.warning}>{optionsError}</MessageBar>}
          <ScopeTree
            financeOptions={financeOptions}
            selectedDirectorates={selectedDirectorates}
            selectedDepartments={selectedDepartments}
            onToggleDirectorate={toggleDirectorate}
            onToggleDepartment={toggleDepartment}
          />
        </>
      )}
    </div>
  );

  const renderScopePill = () => (
    <div className={styles.scopePill}>
      <span className={styles.scopePillLabel}>Finance hierarchy:</span>
      <span className={styles.scopePillValue}>{financeScopeSummary}</span>
      <span style={{ marginLeft: "auto" }}>
        <DefaultButton
          text="Edit on Chat tab"
          onClick={() => setActiveTab("chat")}
          styles={{ root: { minWidth: 0, height: 24, padding: "0 10px", fontSize: 11 } }}
        />
      </span>
    </div>
  );

  const renderTabPanel = (tabKey: string) => {
    const items = catalogByTab.get(tabKey) ?? [];
    const publics = items.filter((i) => i.classification === "public");
    const governeds = items.filter((i) => i.classification === "governed");
    const adminOnly = items.filter((i) => i.classification === "admin_only");

    // Atomic admin-only tabs (Analytics, Admin)
    if (adminOnly.length > 0 && publics.length === 0 && governeds.length === 0) {
      return (
        <div className={styles.emptyState}>
          <div className={`${styles.emptyIcon} ${isAppAdmin ? "" : styles.emptyIconAdmin}`}>
            {isAppAdmin ? "✓" : "★"}
          </div>
          <Text className={styles.emptyTitle}>
            {isAppAdmin ? "Granted via App admin" : "App admins only"}
          </Text>
          <Text className={styles.emptySub}>
            {isAppAdmin
              ? `This user has the App admin role and can access ${TAB_ORDER.find((t) => t.key === tabKey)?.label}.`
              : `Toggle App admin in the header to grant access to ${TAB_ORDER.find((t) => t.key === tabKey)?.label}.`}
          </Text>
        </div>
      );
    }

    // Atomic public tabs (Geospatial, Real Time)
    if (publics.length > 0 && governeds.length === 0) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>✓</div>
          <Text className={styles.emptyTitle}>Auto-granted to all users</Text>
          <Text className={styles.emptySub}>
            The {TAB_ORDER.find((t) => t.key === tabKey)?.label} tab is open by default to every authenticated user. No per-user controls here.
          </Text>
        </div>
      );
    }

    // Tabs with public + governed sections (Chat, Datasets, Dashboards, Build).
    // The scope pill renders whenever the tab has any item that honors the
    // single per-user finance hierarchy — driven by the catalog flag, not
    // hardcoded item names, so adding new hierarchy items is a SQL-only change.
    const hasFinanceItem = governeds.some((g) => g.honors_hierarchy);
    const isChatTab = tabKey === "chat";
    return (
      <Stack tokens={{ childrenGap: 14 }}>
        {publics.length > 0 && (
          <div>
            <Text style={{ fontSize: 13.5, fontWeight: 600, display: "block", marginBottom: 4 }}>
              Public {TAB_ORDER.find((t) => t.key === tabKey)?.label.toLowerCase()}
            </Text>
            <Text variant="small" style={{ color: "#6B6B6B", display: "block", marginBottom: 8 }}>
              Auto-granted. No grant rows in the database.
            </Text>
            <div className={styles.publicRow}>
              <span className={styles.publicTag}>AUTO-GRANTED</span>
              {publics.map((p) => (
                <span key={p.item} className={styles.chip}>
                  {p.display_name}
                </span>
              ))}
            </div>
          </div>
        )}

        {governeds.length > 0 && (
          <div>
            <Text style={{ fontSize: 13.5, fontWeight: 600, display: "block", marginBottom: 4 }}>
              Governed {TAB_ORDER.find((t) => t.key === tabKey)?.label.toLowerCase()}
            </Text>
            <Text variant="small" style={{ color: "#6B6B6B", display: "block", marginBottom: 8 }}>
              {isChatTab
                ? "Pick the domain agents this user can query. Finance hierarchy is set below and reused everywhere."
                : "Each item is granted individually. Finance items honor the hierarchy scope set on the Chat tab."}
            </Text>

            {!isChatTab && hasFinanceItem && renderScopePill()}

            {/* Group governed items by category. When the catalog has any
                category set on this tab, render category headers above each
                group (Finance / Assets etc.). Items without a category fall
                into a default '_uncategorized' group rendered first with no
                header, so flat tabs behave exactly as before. */}
            {(() => {
              const groups = new Map<string, TabCatalogEntry[]>();
              for (const g of governeds) {
                const k = g.category ?? "_uncategorized";
                if (!groups.has(k)) groups.set(k, []);
                groups.get(k)!.push(g);
              }
              const hasCategory = governeds.some((g) => g.category);

              const renderItem = (g: TabCatalogEntry) => {
                const checked = grants.has(grantKey(g.tab, g.item));
                const showHierarchyInline = isChatTab && checked && g.honors_hierarchy;
                const isPii = g.sensitivity === "pii";
                return (
                  <div
                    key={g.item}
                    className={`${styles.grantCard} ${checked ? styles.grantCardChecked : ""}`}
                  >
                    <div className={styles.grantRow}>
                      <Checkbox
                        checked={checked}
                        onChange={(_, val) => toggleGrant(g.tab, g.item, !!val)}
                      />
                      <div className={styles.grantText}>
                        <Text className={styles.grantTitle}>
                          {g.display_name}
                          {isPii && (
                            <span
                              style={{
                                marginLeft: 8,
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: 0.6,
                                color: "#B91C1C",
                                background: "rgba(185,28,28,0.10)",
                                padding: "1px 6px",
                                borderRadius: 3,
                                verticalAlign: "middle",
                              }}
                              title="Personally identifiable information — every query is audit-logged"
                            >
                              PII
                            </span>
                          )}
                        </Text>
                        {g.honors_hierarchy && (
                          <Text className={styles.grantDesc}>
                            Honors the user's finance hierarchy scope.
                          </Text>
                        )}
                        {isPii && !g.honors_hierarchy && (
                          <Text className={styles.grantDesc}>
                            Searches against this entity are audit-logged. Grant only when needed.
                          </Text>
                        )}
                      </div>
                      {g.honors_hierarchy && (
                        <span className={styles.hierarchyBadge}>Honors hierarchy</span>
                      )}
                    </div>
                    {showHierarchyInline && entityType === "user" && renderHierarchyEditor()}
                  </div>
                );
              };

              const result: React.ReactNode[] = [];
              for (const [cat, items] of groups) {
                if (cat !== "_uncategorized" && hasCategory) {
                  result.push(
                    <div
                      key={`__cat_${cat}`}
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                        color: "#5C5C5C",
                        marginTop: 14,
                        marginBottom: 6,
                      }}
                    >
                      {cat}
                    </div>
                  );
                }
                for (const g of items) result.push(renderItem(g));
              }
              return result;
            })()}
          </div>
        )}
      </Stack>
    );
  };

  // ───── Footer ─────

  const onRenderFooter = useCallback(() => (
    <div className={styles.panelFooter}>
      <span className={dirtyCount > 0 ? styles.footerDirty : styles.footerClean}>
        {dirtyCount > 0 ? `${dirtyCount} unsaved change${dirtyCount === 1 ? "" : "s"}` : "No changes"}
      </span>
      {mode === "edit" && entityType === "user" && (
        <DefaultButton
          text="Delete user"
          iconProps={{ iconName: "Delete" }}
          onClick={() => setDeleteDialogOpen(true)}
          disabled={saving || deleting || !upn}
          styles={{
            root: {
              borderColor: "#A4262C",
              color: "#A4262C",
              marginRight: "auto", // push to the left so it sits apart from Cancel/Save
            },
            rootHovered: { backgroundColor: "#FCE7E9", color: "#A4262C", borderColor: "#A4262C" },
            rootPressed: { backgroundColor: "#F1B0B7", color: "#A4262C", borderColor: "#A4262C" },
          }}
        />
      )}
      <DefaultButton text="Cancel" onClick={handleDismiss} disabled={saving || deleting} />
      <PrimaryButton
        text={saving ? "Saving…" : "Save changes"}
        onClick={handleSave}
        disabled={saving || deleting || dirtyCount === 0 || !upn}
      />
    </div>
  ), [dirtyCount, handleDismiss, handleSave, saving, deleting, upn, mode, entityType]);

  // ───── Render ─────

  const headerText =
    mode === "addAdmin"
      ? "Add app admin"
      : mode === "addGroup"
      ? "Add security group"
      : mode === "add"
      ? "Add user"
      : `Edit access — ${displayName || upn}`;

  return (
    <>
      <Panel
        isOpen={open}
        onDismiss={handleDismiss}
        type={PanelType.custom}
        customWidth="980px"
        headerText={headerText}
        closeButtonAriaLabel="Close"
        isBlocking
        isFooterAtBottom
        onRenderFooterContent={onRenderFooter}
      >
        <div className={styles.panelBody}>
          {error && (
            <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
              {error}
            </MessageBar>
          )}
          {catalogError && (
            <MessageBar messageBarType={MessageBarType.error}>{catalogError}</MessageBar>
          )}

          {/* Identity */}
          <div className={styles.identityRow}>
            {isAddMode ? (
              <div ref={searchBoxRef} style={{ position: "relative" }}>
                <Label>{entityType === "group" ? "Search Entra group" : "Search user"}</Label>
                <SearchBox
                  value={searchQuery}
                  onChange={(_, val) => {
                    setSearchQuery(val ?? "");
                    if (upn) {
                      setUpn("");
                      setDisplayName("");
                    }
                  }}
                  onClear={() => {
                    setSearchQuery("");
                    setUpn("");
                    setDisplayName("");
                    setShowResults(false);
                  }}
                  placeholder={entityType === "group" ? "Group name…" : "Name or UPN…"}
                  autoComplete="off"
                  disableAnimation
                />
                {searching && (
                  <Text variant="small" style={{ color: "#605E5C", marginTop: 4 }}>
                    Searching…
                  </Text>
                )}
                {graphError && (
                  <Text variant="small" style={{ color: "#A4262C" }}>
                    {graphError}
                  </Text>
                )}
                {showResults && searchResults.length > 0 && (
                  <Callout
                    target={searchBoxRef.current}
                    onDismiss={() => setShowResults(false)}
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    isBeakVisible={false}
                    gapSpace={2}
                  >
                    <div className={styles.resultsList}>
                      {searchResults.map((r) => (
                        <div
                          key={r.upn}
                          className={styles.resultRow}
                          onClick={() => pickSearchResult(r)}
                        >
                          <Text className={styles.resultName}>{r.displayName}</Text>
                          <Text className={styles.resultUpn}>{r.upn}</Text>
                        </div>
                      ))}
                    </div>
                  </Callout>
                )}
              </div>
            ) : (
              <TextField
                label="UPN"
                value={upn}
                readOnly
                styles={{ field: { background: "#F8F7F5" } }}
              />
            )}
            <TextField
              label="Display name"
              value={displayName}
              readOnly={!isAddMode}
              onChange={(_, val) => setDisplayName(val ?? "")}
              styles={!isAddMode ? { field: { background: "#F8F7F5" } } : undefined}
            />
          </div>

          {/* Settings row — App admin only in edit-user mode; Token budget
              everywhere except addAdmin (groups can carry budgets too). */}
          {mode !== "addAdmin" && (
            <div className={styles.settingsRow}>
              {mode === "edit" && entityType === "user" && (
                <label className={styles.settingsCard}>
                  <span className={styles.settingsCardLabel}>App admin</span>
                  <Toggle
                    checked={isAppAdmin}
                    onChange={(_, checked) => setIsAppAdmin(!!checked)}
                    onText="Yes — Analytics + Admin granted"
                    offText="No"
                  />
                  <Text className={styles.settingsCardHint}>
                    Admin role grants Analytics + Admin tabs automatically.
                  </Text>
                </label>
              )}
              {mode === "edit" && entityType === "user" && (
                <label className={styles.settingsCard}>
                  <span className={styles.settingsCardLabel}>Account active</span>
                  <Toggle
                    checked={isActive}
                    onChange={(_, checked) => setIsActive(!!checked)}
                    onText="Active — can sign in"
                    offText="Disabled — sign-in blocked"
                  />
                  <Text className={styles.settingsCardHint}>
                    Turn off to block this user from all portal surfaces. Stronger than removing grants — they get a 403 on every authenticated request. Existing grants are preserved for fast re-enable.
                  </Text>
                </label>
              )}
              <label className={styles.settingsCard}>
                <span className={styles.settingsCardLabel}>Monthly AI budget</span>
                <TextField
                  type="number"
                  value={monthlyBudget}
                  onChange={(_, val) => setMonthlyBudget(val ?? "")}
                  prefix="AUD"
                  placeholder="Unlimited"
                  min={0}
                />
                <Text className={styles.settingsCardHint}>
                  Per-month dollar cap on AI usage. Blank = unlimited.
                </Text>
              </label>
            </div>
          )}

          {/* In addAdmin mode the panel is single-purpose — just confirm the
              admin grant. Tabs are hidden entirely; only Identity + this
              banner + Save are visible. */}
          {isAdminFlow ? (
            <MessageBar
              messageBarType={MessageBarType.info}
              isMultiline
              styles={{ root: { marginBottom: 12 } }}
            >
              <Text>
                <strong>Granting App admin to this user.</strong> They'll get
                full access to the Admin and Analytics tabs in the portal,
                plus override on every governed grant. Save to confirm.
              </Text>
            </MessageBar>
          ) : catalogLoading ? (
            <Spinner size={SpinnerSize.medium} label="Loading catalog…" />
          ) : (
            <Pivot
              selectedKey={activeTab}
              onLinkClick={(item) => item && setActiveTab(item.props.itemKey ?? "chat")}
              styles={{ root: { marginBottom: 12 } }}
            >
              {TAB_ORDER.map((t) => {
                const items = catalogByTab.get(t.key) ?? [];
                const isAdminTab = items.length > 0 && items.every((i) => i.classification === "admin_only");
                const count = tabGovernedCounts[t.key] ?? 0;
                const headerText =
                  isAdminTab
                    ? `${t.label} 🔒`
                    : count > 0
                    ? `${t.label} (${count})`
                    : t.label;
                return (
                  <PivotItem key={t.key} itemKey={t.key} headerText={headerText}>
                    <div style={{ paddingTop: 12 }}>{renderTabPanel(t.key)}</div>
                  </PivotItem>
                );
              })}
            </Pivot>
          )}
        </div>
      </Panel>

      {/* Discard-confirm dialog */}
      <Dialog
        hidden={!discardDialogOpen}
        onDismiss={() => setDiscardDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: "Discard unsaved changes?",
          subText: `You have ${dirtyCount} unsaved change${dirtyCount === 1 ? "" : "s"}. Closing will lose them.`,
        }}
      >
        <DialogFooter>
          <DefaultButton onClick={() => setDiscardDialogOpen(false)} text="Keep editing" />
          <PrimaryButton
            onClick={() => {
              setDiscardDialogOpen(false);
              onClose();
            }}
            text="Discard"
          />
        </DialogFooter>
      </Dialog>

      {/* Delete-confirm dialog (hard delete across all access.* tables) */}
      <Dialog
        hidden={!deleteDialogOpen}
        onDismiss={() => !deleting && setDeleteDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: "Delete this user from the portal?",
          subText:
            `${upn} will be removed from every access table: grants, admin role, finance scope, token budget, and the active/disabled flag. ` +
            `The audit log of past changes is preserved. The user will lose access immediately on their next request.`,
        }}
      >
        <DialogFooter>
          <DefaultButton
            onClick={() => setDeleteDialogOpen(false)}
            text="Cancel"
            disabled={deleting}
          />
          <PrimaryButton
            onClick={handleDelete}
            disabled={deleting}
            text={deleting ? "Deleting…" : "Delete permanently"}
            styles={{
              root: { backgroundColor: "#A4262C", borderColor: "#A4262C" },
              rootHovered: { backgroundColor: "#8E2227", borderColor: "#8E2227" },
              rootPressed: { backgroundColor: "#751D22", borderColor: "#751D22" },
            }}
          />
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default EditAccessPanel;
